import { useState, useCallback, useMemo, useEffect } from 'react';

function storageKeyFor(tableId) {
    return tableId ? `ut-hidden-columns:${tableId}` : null;
}

function readStored(storageKey) {
    if (!storageKey) return null;
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function writeStored(storageKey, keys) {
    if (!storageKey) return;
    try {
        if (!keys || !keys.length) {
            window.localStorage.removeItem(storageKey);
        } else {
            window.localStorage.setItem(storageKey, JSON.stringify(keys));
        }
    } catch {
        // localStorage unavailable (private mode, quota, SSR) — no-op
    }
}

/**
 * Tracks which columns are hidden, mirroring the shape of useColumnResize /
 * useColumnReorder: internal state by default, or fully controlled via
 * `controlledHidden` + `onHiddenColumnsChange`. When a `tableId` is given,
 * the hidden-columns set is persisted to localStorage under
 * `ut-hidden-columns:<tableId>` (uncontrolled mode only).
 */
export default function useColumnVisibility({
    tableId,
    columns = [],
    onHiddenColumnsChange,
    controlledHidden,
} = {}) {
    const storageKey = useMemo(() => storageKeyFor(tableId), [tableId]);

    const [internalHidden, setInternalHidden] = useState(() => readStored(storageKey) || []);

    const hiddenColumns = controlledHidden ?? internalHidden;
    const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

    // Re-read from storage if the table identity changes.
    useEffect(() => {
        if (controlledHidden) return;
        setInternalHidden(readStored(storageKey) || []);
    }, [storageKey, controlledHidden]);

    const updateHidden = useCallback(
        (next) => {
            if (controlledHidden && onHiddenColumnsChange) {
                onHiddenColumnsChange(next);
            } else {
                setInternalHidden(next);
                writeStored(storageKey, next);
                if (onHiddenColumnsChange) onHiddenColumnsChange(next);
            }
        },
        [controlledHidden, onHiddenColumnsChange, storageKey]
    );

    const toggleColumn = useCallback(
        (key) => {
            const col = columns.find((c) => c.key === key);
            if (col && col.hideable === false) return;
            const next = hiddenSet.has(key)
                ? hiddenColumns.filter((k) => k !== key)
                : [...hiddenColumns, key];
            updateHidden(next);
        },
        [columns, hiddenSet, hiddenColumns, updateHidden]
    );

    const showAll = useCallback(() => {
        updateHidden([]);
    }, [updateHidden]);

    const isVisible = useCallback((key) => !hiddenSet.has(key), [hiddenSet]);

    return { hiddenColumns, hiddenSet, isVisible, toggleColumn, showAll };
}
